"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { CircleQuestionMark as HelpCircle, MessageSquare, PhoneCall, ArrowRight, Search, MapPin, CircleCheck as CheckCircle2 } from 'lucide-react';
const leftFaqItems = [{
  id: "item-1",
  question: "Why does my Luton business need local SEO instead of standard national SEO?",
  answer: "Local SEO targets high-intent searchers specifically within Luton, Dunstable, and Bedfordshire. While national SEO focuses on broad UK terms, local SEO optimizes your Google Business Profile, local citations, and geo-targeted pages to put you directly in front of nearby customers looking to hire or buy right now."
}, {
  id: "item-2",
  question: "Will I work directly with you, Yassine, or be handed off to a junior team?",
  answer: "You work directly with me from day one. Unlike traditional agencies where senior consultants win the account and junior account managers execute, I personally handle your Luton SEO audit, strategy, technical fixes, and ongoing optimization."
}, {
  id: "item-3",
  question: "How quickly can a Luton business expect to see local SEO results?",
  answer: "Most local clients in Luton see initial visibility improvements within 60 to 90 days, especially in Google Maps and local 3-pack listings. Broader organic search ranking gains for competitive Luton commercial terms typically accelerate between months 3 and 6."
}, {
  id: "item-4",
  question: "How do you tailor your strategies specifically to Luton's market?",
  answer: "I analyze search intent specific to Luton's commercial sectors, geographic hubs (such as the town centre, airport district, and industrial corridors), and regional customer behavior. I build tailored local schemas and content that reflect real local buyer queries."
}, {
  id: "item-5",
  question: "How do you optimize my Google Business Profile (GBP) for Luton searches?",
  answer: "I conduct a thorough GBP audit, refining primary and secondary category signals, optimizing business descriptions, geo-tagging media, configuring service area perimeters, and setting up systematic review workflows designed for Luton customers."
}, {
  id: "item-6",
  question: "Do you support service-area businesses across Luton and Bedfordshire?",
  answer: "Yes. I work extensively with service-area businesses—such as plumbers, legal firms, IT suppliers, and contractors—covering Luton, Dunstable, Harpenden, and broader Bedfordshire without relying on spammy or duplicate location pages."
}];
const rightFaqItems = [{
  id: "item-7",
  question: "How does AI Search (Google AI Overviews & ChatGPT) affect local Luton rankings?",
  answer: "AI search tools prioritize structured entity data, consistent business info across UK directories, authoritative local mentions, and real client reviews. My strategies build strong local brand entities so your business gets recommended in AI Overviews alongside traditional map packs."
}, {
  id: "item-8",
  question: "Can Local SEO help my business outrank large national competitors in Luton?",
  answer: "Yes. Google's algorithm heavily prioritizes local relevance and geographic proximity for local intent searches. A well-optimized independent Luton business with strong local signals can consistently beat national brands relying on generic UK pages."
}, {
  id: "item-9",
  question: "What is included in your initial Luton Local SEO Audit?",
  answer: "My local audit covers your Google Business Profile health, local keyword rankings, technical site integrity, NAP consistency across UK citations, on-page localized signals, backlink authority, and a direct analysis of your main Luton competitors."
}, {
  id: "item-10",
  question: "How do you track and report on local SEO performance?",
  answer: "I deliver transparent monthly reporting showing keyword map movement, local organic traffic, phone calls, direction requests, and contact form submissions. No fluff—just clear metrics tied to your business growth in Luton."
}, {
  id: "item-11",
  question: "What makes your freelance consultancy better than hiring a Luton agency?",
  answer: "You get senior-level expertise, direct consultant access, no agency overhead markups, and flexible engagement without long-term restrictive contracts. I limit client capacity so I can give your Luton business full focus."
}, {
  id: "item-12",
  question: "How do I get started with a free consultation for my Luton business?",
  answer: "Simply book a free 30-minute consultation through my calendar or drop me a message. I will review your current local search footprint before our call so we can focus on practical, high-impact growth opportunities."
}];
export default function LutonFaq() {
  const [searchQuery, setSearchQuery] = useState("");
  const filterItems = (items: typeof leftFaqItems) => {
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item => item.question.toLowerCase().includes(query) || item.answer.toLowerCase().includes(query));
  };
  const filteredLeft = filterItems(leftFaqItems);
  const filteredRight = filterItems(rightFaqItems);
  const totalResults = filteredLeft.length + filteredRight.length;
  return <section id="localseofaqluton" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {}
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-primary/5 blur-3xl -z-10 rounded-full" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-96 h-96 bg-accent/10 blur-3xl -z-10 rounded-full" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary bg-secondary border-secondary">
              <MapPin className="size-3.5 text-primary" />
              Luton Local SEO FAQs
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight leading-tight mb-6">
            Clear Answers to Your <span className="text-primary">Luton Local SEO</span> Questions
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Everything you need to know about capturing local Google searches in Luton, Dunstable, and Bedfordshire. You work directly with me—no agency middleman, just honest advice and real strategy.
          </motion.p>

          {}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="mt-8 relative max-w-md mx-auto">
            <div className="relative flex items-center">
              <Search className="absolute left-4 size-4 text-muted-foreground pointer-events-none" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search Luton SEO questions..." className="w-full pl-11 pr-4 py-3 bg-card text-foreground placeholder:text-muted-foreground text-sm rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-[color,border-color,box-shadow] shadow-2xs" />
              {searchQuery && <Button variant="ghost" size="sm" onClick={() => setSearchQuery("")} className="absolute right-2 text-xs text-muted-foreground hover:text-foreground h-7 px-2">
                  Clear
                </Button>}
            </div>
            {searchQuery && <p className="text-xs text-muted-foreground mt-2 text-left px-1">
                Found {totalResults} matching question{totalResults === 1 ? "" : "s"}
              </p>}
          </motion.div>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
          {}
          <div className="space-y-4">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {filteredLeft.map((item, i) => <motion.div key={item.id} data-index={i} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }}>
                  <AccordionItem value={item.id} className="bg-card border border-border rounded-xl px-5 py-1 shadow-2xs transition-[border-color,box-shadow] hover:border-primary/40 data-[state=open]:border-primary/60 data-[state=open]:shadow-md">
                    <AccordionTrigger className="text-left font-semibold text-base sm:text-lg text-primary hover:no-underline hover:text-primary/90 py-4 gap-4">
                      <span className="flex items-start gap-3">
                        <HelpCircle className="size-5 shrink-0 text-primary mt-0.5" />
                        <span>{item.question}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-8 pr-2 pb-5">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>)}
            </Accordion>
            {filteredLeft.length === 0 && <div className="p-8 text-center bg-card rounded-xl border border-border text-muted-foreground text-sm">
                No matching questions found in this section.
              </div>}
          </div>

          {}
          <div className="space-y-4">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {filteredRight.map((item, i) => <motion.div key={item.id} data-index={i + filteredLeft.length} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }}>
                  <AccordionItem value={item.id} className="bg-card border border-border rounded-xl px-5 py-1 shadow-2xs transition-[border-color,box-shadow] hover:border-primary/40 data-[state=open]:border-primary/60 data-[state=open]:shadow-md">
                    <AccordionTrigger className="text-left font-semibold text-base sm:text-lg text-primary hover:no-underline hover:text-primary/90 py-4 gap-4">
                      <span className="flex items-start gap-3">
                        <HelpCircle className="size-5 shrink-0 text-primary mt-0.5" />
                        <span>{item.question}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-8 pr-2 pb-5">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>)}
            </Accordion>
            {filteredRight.length === 0 && <div className="p-8 text-center bg-card rounded-xl border border-border text-muted-foreground text-sm">
                No matching questions found in this section.
              </div>}
          </div>
        </div>

        {}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mt-16 lg:mt-20 bg-card border border-border rounded-2xl p-6 sm:p-8 lg:p-10 shadow-md relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-12 -mr-12 size-64 bg-primary/5 rounded-full blur-2xl pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8 text-center lg:text-left">
            <div className="space-y-3 max-w-2xl">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <CheckCircle2 className="size-4 text-primary" />
                Direct SEO Expertise
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
                Have a Specific Question About Your Luton Business?
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                I am happy to review your current Google Business Profile or website search visibility and offer practical guidance before you commit to anything.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto gap-2 text-sm font-semibold">
                <Link to="/ContactPage">
                  <MessageSquare className="size-4" />
                  Book a Free Consultation
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto gap-2 text-sm font-medium">
                <a href="tel:+447490984430">
                  <PhoneCall className="size-4" />
                  +44 7490984430
                </a>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}